import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine} from '@dropbox/dig-icons/assets';
import type {Workstream} from 'client';
import {t} from 'i18next';
import {useCallback, useMemo, useRef, useState} from 'react';

import {AddSubworkstreamField} from './AddSubworkstreamField';
import {SectionLabel} from './SectionLabel';
import {SortableSubworkstreamGroup} from './SortableSubworkstreamGroup';
import {SubworkstreamRemovalContext, useSubworkstreamEdits} from './SubworkstreamEdits';
import {usePointerBand} from './usePointerBand';
import {WorkstreamListTable} from './WorkstreamListTable';
import {isArchived} from './WorkstreamScanList';

/**
 * /workstreams: the detail page's Sub-workstreams. Always shown (even
 * with none) so there's a place to add one: a quiet + after the label opens the
 * inline find-or-create field. Cards are in their saved order: a pointer
 * anywhere level with the section (out in the page margins too) shows every
 * card's drag handle (left) to reorder; a card's hover shows an unlink (right)
 * to remove it. `asTable` (`?table=1`) lists them as the table's rows instead,
 * read-only and in their saved order; the + still adds one.
 */
export const SubworkstreamsSection = ({
  workstream,
  onLeaveUpdate,
  asTable = false,
}: {
  workstream: Workstream;
  onLeaveUpdate: (id: number) => void;
  asTable?: boolean;
}) => {
  const [adding, setAdding] = useState(false);
  const edits = useSubworkstreamEdits(workstream);
  // Saved order, as the API returns them (what expanded cards and /workstreams show).
  const subs = useMemo(
    () => (workstream.dependencies ?? []).map(({dependent}) => dependent),
    [workstream.dependencies]
  );
  const sectionRef = useRef<HTMLDivElement>(null);
  // The card the pointer is level with: its handle stands out from the rest.
  const rowAt = useCallback((pointerY: number) => {
    const rows = sectionRef.current?.querySelectorAll<HTMLElement>('[data-sortable-row]') ?? [];
    for (const row of rows) {
      const {top, bottom} = row.getBoundingClientRect();
      if (pointerY >= top && pointerY < bottom) return Number(row.dataset.sortableRow);
    }
    return null;
  }, []);
  const pointerRowId = usePointerBand(sectionRef, rowAt);

  return (
    <Box ref={sectionRef}>
      <SectionLabel
        action={
          !adding && (
            <Tooltip title={t('add_sub_workstream')} placement="top">
              <IconButton
                variant="transparent"
                size="small"
                aria-label={t('add_sub_workstream')}
                onClick={() => setAdding(true)}
              >
                <UIIcon src={AddLine} size="small" className={atoms({color: 'Text Subtle'})} />
              </IconButton>
            </Tooltip>
          )
        }
      >
        Sub-workstreams
      </SectionLabel>
      {adding && <AddSubworkstreamField workstream={workstream} onDone={() => setAdding(false)} />}
      {subs.length > 0 && asTable ? (
        <WorkstreamListTable
          workstreams={subs.filter((sub) => !isArchived(sub))}
          loading={false}
          embedded
          keepOrder
          onLeaveUpdate={onLeaveUpdate}
        />
      ) : (
        subs.length > 0 && (
          <SubworkstreamRemovalContext.Provider value={edits}>
            <SortableSubworkstreamGroup
              workstreams={subs.filter((sub) => !isArchived(sub))}
              archived={subs.filter(isArchived)}
              pointerRowId={pointerRowId}
              onReorder={edits.reorder}
              onLeaveUpdate={onLeaveUpdate}
            />
          </SubworkstreamRemovalContext.Provider>
        )
      )}
    </Box>
  );
};
